import * as React from 'react';
import type { Echo } from '@/types';
import { ChatHeader } from './ChatHeader';
import { useEchoStatsQuery } from '@/hooks/useEchoStatsQuery';
import { formatCompactNumber } from '@/utils/formatters';
import { useToast } from '@/context/ToastContext';

interface ChatHeaderContainerProps {
  echo: Echo;
  onClear?: () => void;
  disabled?: boolean;
  onOpenSidebar?: () => void;
  isTransitioning?: boolean;
  children?: React.ReactNode;
  isAudioEnabled?: boolean;
  onToggleAudio?: () => void;
  isVideoEnabled?: boolean;
  onToggleVideo?: () => void;
  compact?: boolean;
}

export const ChatHeaderContainer: React.FC<ChatHeaderContainerProps> = ({
  echo,
  onClear,
  disabled,
  onOpenSidebar,
  isTransitioning = false,
  children,
  isAudioEnabled = false,
  onToggleAudio,
  isVideoEnabled = false,
  onToggleVideo,
  compact = false,
}) => {
  const [isClearModalOpen, setIsClearModalOpen] = React.useState(false);
  const { addToast } = useToast();

  const { data: statsData } = useEchoStatsQuery(echo.id);
  const totalFans = statsData?.total_fans ?? 0;
  const formattedFans = formatCompactNumber(totalFans) || '0';

  const handleAudioToggle = () => {
    if (onToggleAudio) {
      onToggleAudio();
    } else {
      addToast({
        title: 'Coming Soon',
        subtitle: 'Audio calling is currently under development.',
        type: 'info',
      });
    }
  };

  return (
    <ChatHeader
      echo={echo}
      formattedFans={formattedFans}
      onClear={onClear}
      onClearModalOpen={() => setIsClearModalOpen(true)}
      onClearModalClose={() => setIsClearModalOpen(false)}
      isClearModalOpen={isClearModalOpen}
      disabled={disabled}
      onOpenSidebar={onOpenSidebar}
      isTransitioning={isTransitioning}
      isAudioEnabled={isAudioEnabled}
      onAudioToggle={handleAudioToggle}
      isVideoEnabled={isVideoEnabled}
      // eslint-disable-next-line @typescript-eslint/no-empty-function -- noop fallback
      onVideoToggle={onToggleVideo ?? (() => {})}
      videoMessagesEnabled={echo.videoMessagesEnabled ?? false}
      compact={compact}
    >
      {children}
    </ChatHeader>
  );
};
